import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_URL, APP_CONFIG, ConfigSettings } from '@shared/config';
import { Filters, GlobalFilterGroup } from '@shared/models';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export class FiltersService {
  globalSettings: ConfigSettings;
  filtersFormatted: Observable<string>;

  private readonly urlGlobalFiltersGroup: string;

  constructor(
    private http: HttpClient,
    @Inject(APP_CONFIG) settings: ConfigSettings
  ) {
    this.globalSettings = settings;
    this.urlGlobalFiltersGroup = `${this.globalSettings.orApiUrl}${API_URL.filtersGroup}`;
  }

  getDimensionPath(dimension: string): Observable<any> {
    return this.http.get(
      `${this.urlGlobalFiltersGroup}?dimension=${dimension}`,
      { withCredentials: true }
    );
  }

  getAllFilters(): Observable<GlobalFilterGroup[]> {
    return this.http.get<GlobalFilterGroup[]>(`${this.urlGlobalFiltersGroup}`, {
      withCredentials: true,
    });
  }

  getFiltersFormatted(filters: Filters): Observable<string[]> {
    const observables = Object.entries(filters).map(
      ([name, value]: [string, string[]]) =>
        this.getDimensionPath(name).pipe(
          map((result: string[]) => ({
            filterType: result[1],
            filterValue: this.processFilterValue(value, name),
            error: false,
          })),
          catchError(() =>
            of({
              filterType: '',
              filterValue: '',
              error: true,
            })
          )
        )
    );

    const groupFilterValues = result => {
      return result.reduce(
        (filtersValue, { filterValue, filterType, error }) => {
          if (error) return { ...filterValue };
          if (!filtersValue[filterType]) filtersValue[filterType] = [];
          return {
            ...filtersValue,
            [filterType]: filtersValue[filterType].concat(filterValue),
          };
        },
        {}
      );
    };

    const formatFilters = result => {
      const filterFormatted = [];
      Object.entries(result).forEach(([key, value]: [string, string[]]) => {
        filterFormatted.push(`${key} - ${value.join(', ')} `);
      });
      return filterFormatted;
    };

    return forkJoin(observables).pipe(
      map(groupFilterValues),
      map(formatFilters)
    );
  }

  processFilterValue(value: string[], name: string): string[] {
    switch (name) {
      case 'quarter':
        // Add the Q to selected quarter filters (Q1).
        return value.map(selectedFilter => `Q${selectedFilter}`);
      case 'calendarSemester':
        // Add the S to selected semester filters (S1).
        return value.map(selectedFilter => `S${selectedFilter}`);
      default:
        return value;
    }
  }
}
